import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select, type SelectOption } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { goalSubSectorsApi, goalsApi } from '../budgetGoalsApi';
import { sampleGoal } from '../utils/sampleBudgetCatalogs';

/**
 * Create or edit one goal, with the GOS triad the investment programme prints.
 *
 * The OBJECTIVE is required and the STRATEGY is not: a goal row with no objective states
 * nothing a plan can be built on, while a goal is routinely declared before anyone has written
 * down how it will be reached.
 *
 * Both are `<Textarea>` rather than `<RichTextField>`: they render in a picker option's
 * sublabel and in a table cell, and rich text is for prose somebody writes, re-reads and
 * STRUCTURES. They are sentences, not documents.
 */
export function GoalModal({
  open,
  editing,
  /** Seeds and LOCKS the sub-sector, for a create opened from a sub-sector's own row. */
  lockedSubSector,
  onClose,
  onSaved,
}: {
  open: boolean;
  /**
   * The row being corrected, or null to create.
   *
   * **Typed as the fields this form READS**, so the settings page's `GoalListItem` and the
   * Goal Setting tree's leaf-plus-its-ancestors both satisfy it. One modal, two surfaces.
   */
  editing: {
    id: string;
    name: string;
    objective: string;
    strategy: string | null;
    goalSubSectorId: string;
    subSectorName: string;
    sectorName: string;
  } | null;
  lockedSubSector?: { id: string; name: string; sectorName: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [subSectorId, setSubSectorId] = useState(editing?.goalSubSectorId ?? lockedSubSector?.id ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [objective, setObjective] = useState(editing?.objective ?? '');
  const [strategy, setStrategy] = useState(editing?.strategy ?? '');

  const [subSectors, setSubSectors] = useState<SelectOption[]>(() => {
    if (lockedSubSector) {
      return [
        {
          value: lockedSubSector.id,
          label: lockedSubSector.name,
          sublabel: lockedSubSector.sectorName,
        },
      ];
    }

    if (editing) {
      return [
        { value: editing.goalSubSectorId, label: editing.subSectorName, sublabel: editing.sectorName },
      ];
    }

    return [];
  });

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (lockedSubSector) return;

    const controller = new AbortController();

    goalSubSectorsApi
      .options(controller.signal)
      .then((rows) => {
        if (controller.signal.aborted) return;

        // The SECTOR is the sublabel: two sub-sectors may legitimately share a name, and in a
        // flat picker there is nothing else to tell them apart.
        const options = rows.map((r) => ({
          value: r.id,
          label: r.name,
          sublabel: r.sectorName,
        }));

        // A goal filed under a since-deleted sub-sector keeps it, so the edit form injects it
        // rather than letting the field state that nothing is set.
        if (editing && !options.some((o) => o.value === editing.goalSubSectorId)) {
          options.push({
            value: editing.goalSubSectorId,
            label: editing.subSectorName,
            sublabel: editing.sectorName,
          });
        }

        setSubSectors(options);
      })
      .catch(() => {
        // Fails soft: the field keeps whatever it was seeded with.
      });

    return () => controller.abort();
  }, [editing, lockedSubSector]);

  const valid = subSectorId.length > 0 && name.trim().length > 0 && objective.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      goalSubSectorId: subSectorId,
      name: name.trim(),
      objective: objective.trim(),
      strategy: strategy.trim() || null,
    };

    try {
      if (editing) await goalsApi.update(editing.id, request);
      else await goalsApi.create(request);

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the goal.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'goal' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit goal' : 'New goal'}
      size="lg"
      id="goal-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = sampleGoal();
          setName(sample.name);
          setObjective(sample.objective);
          setStrategy(sample.strategy);

          /*
            The SUB-SECTOR is filled too — the contract is that the primary action ENABLES on
            the first try, and on an unlocked create this is a required field nobody has
            answered. Picked from the options the form already loaded, so it resolves against
            the live catalog rather than being an id the server will refuse.

            A LOCKED sub-sector, or one the reader has already chosen, is left alone.
          */
          if (!lockedSubSector && !subSectorId && subSectors.length > 0) {
            setSubSectorId(subSectors[Math.floor(Math.random() * subSectors.length)].value);
          }
        },
        disabled: saving,
      }}
      confirm={{
        id: 'goal-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="goal-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="goal-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <Select
          id="goal-goal-sub-sector-id"
          label="Sub-sector"
          required
          description={
            lockedSubSector
              ? 'Set from the sub-sector this was opened on. Create from the Goals page to choose freely.'
              : 'The rung above this one. A goal name is held once within its own sub-sector.'
          }
          value={subSectorId || null}
          onChange={setSubSectorId}
          options={subSectors}
          sort="none"
          clearable={false}
          disabled={saving || Boolean(lockedSubSector)}
          error={error?.fieldError('goalSubSectorId')}
          /*
            NO create-in-place here, unlike the sector picker one rung up. A sub-sector needs a
            SECTOR as well as a name, so `allowCustom` — which writes a row from the typed text
            alone — cannot supply it. The honest shapes are a `renderCreateForm` panel or a
            trip to the Sub-sectors page, and until one is built the picker says so rather than
            offering a create that would file the row under nothing.
          */
        />

        <TextField
          id="goal-name"
          label="Name"
          required
          description="What the goal is called. Held once within its sub-sector."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={saving}
          error={error?.fieldError('name')}
        />

        <Textarea
          id="goal-objective"
          label="Objective"
          required
          description="What achieving the goal is meant to accomplish. The investment programme prints a goal with the objective it serves."
          value={objective}
          onChange={(e) => setObjective(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('objective')}
        />

        <Textarea
          id="goal-strategy"
          label="Strategy"
          description="How the objective is to be reached. Optional — a goal is often declared before its strategy is written."
          value={strategy}
          onChange={(e) => setStrategy(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('strategy')}
        />
      </div>
    </Modal>
  );
}
